import { Box, Tooltip as MUITooltip } from '@mui/material';
import { getNoShowKpiRequest } from 'api/requests/kpis-requests';
import { UPInlineDropdown } from 'components/dropdown';
import { NoShowReasonFilter } from 'constants/NoShowReasonFilter';
import { getISO8601WeekNumber } from 'modules/contracts/helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  DASHBOARD__KPI_FETCH_ERROR,
  DASHBOARD__NO_SHOW_KPI,
  DASHBOARD__NO_SHOW_KPI_ALL,
  DASHBOARD__NO_SHOW_KPI_ALLOWED_ABSENCE,
  DASHBOARD__NO_SHOW_KPI_NOT_ALLOWED_ABSENCE,
  DASHBOARD__NO_SHOW_KPI_OTHER,
  DASHBOARD__NO_SHOW_KPI_SICKNESS,
  DASHBOARD__NO_SHOW_KPI_TOP_COMPANIES,
  HEADER__WEEK,
} from 'translations/constants';
import { INoShowKpiBucket, INoShowTopCompany } from 'types/kpis/INoShowKpiBucket';
import { ReactComponent as WarningIcon } from '../../../../assets/icons/ContractErrorIcon.svg';
import {
  NoShowKpiChartContainer,
  NoShowKpiHeader,
  NoShowKpiSection,
  NoShowKpiTitle,
  TooltipContainer,
  TooltipRow,
  TooltipTitle,
} from './styles';

interface ChartPoint {
  name: string;
  percentage: number;
  topCompanies: INoShowTopCompany[];
}

const getCurrentMondayIsoDate = (): string => {
  const today = new Date();
  const dayOfWeek = (today.getDay() + 6) % 7;
  today.setDate(today.getDate() - dayOfWeek);
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DashboardNoShowKpiTile = (): JSX.Element => {
  const [t] = useTranslation();

  const [reasonFilter, setReasonFilter] = useState<string>(String(NoShowReasonFilter.ALL));
  const [buckets, setBuckets] = useState<INoShowKpiBucket[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setHasError(false);
    getNoShowKpiRequest({ reasonFilter: Number(reasonFilter) as NoShowReasonFilter })
      .then(data => {
        if (!cancelled) setBuckets(data);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setBuckets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [reasonFilter]);

  const dropdownItems = useMemo(
    () => [
      { value: String(NoShowReasonFilter.ALL), label: t(DASHBOARD__NO_SHOW_KPI_ALL) },
      { value: String(NoShowReasonFilter.ALLOWED_ABSENCE), label: t(DASHBOARD__NO_SHOW_KPI_ALLOWED_ABSENCE) },
      { value: String(NoShowReasonFilter.NOT_ALLOWED_ABSENCE), label: t(DASHBOARD__NO_SHOW_KPI_NOT_ALLOWED_ABSENCE) },
      { value: String(NoShowReasonFilter.SICKNESS), label: t(DASHBOARD__NO_SHOW_KPI_SICKNESS) },
      { value: String(NoShowReasonFilter.OTHER), label: t(DASHBOARD__NO_SHOW_KPI_OTHER) },
    ],
    [t],
  );

  const currentMondayIso = useMemo(getCurrentMondayIsoDate, []);

  const chartData = useMemo<ChartPoint[]>(() => {
    const source = hasError ? [] : buckets;
    return source.map(b => ({
      name: `${t(HEADER__WEEK)} ${getISO8601WeekNumber(new Date(`${b.weekStartDate}T00:00:00`))}`,
      percentage: b.percentage,
      topCompanies: b.topCompanies ?? [],
    }));
  }, [buckets, hasError, t]);

  const currentWeekIndex = useMemo(
    () => buckets.findIndex(b => b.weekStartDate === currentMondayIso),
    [buckets, currentMondayIso],
  );

  const CustomTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const point = payload[0]?.payload as ChartPoint;
      if (!point) return null;
      return (
        <TooltipContainer>
          <TooltipTitle>{t(DASHBOARD__NO_SHOW_KPI_TOP_COMPANIES)}</TooltipTitle>
          {point.topCompanies.length === 0 ? (
            <TooltipRow>
              <span>—</span>
            </TooltipRow>
          ) : (
            point.topCompanies.map(c => (
              <TooltipRow key={c.companyName}>
                <span>{c.companyName}</span>
                <span>{`${c.percentage}%`}</span>
              </TooltipRow>
            ))
          )}
        </TooltipContainer>
      );
    },
    [t],
  );

  return (
    <NoShowKpiSection>
      <NoShowKpiHeader>
        <Box display="flex" alignItems="center" gap="6px">
          <NoShowKpiTitle>{t(DASHBOARD__NO_SHOW_KPI)}</NoShowKpiTitle>
          {hasError && (
            <MUITooltip title={t(DASHBOARD__KPI_FETCH_ERROR)} placement="right" arrow>
              <Box display="flex" alignItems="center">
                <WarningIcon width={20} height={20} />
              </Box>
            </MUITooltip>
          )}
        </Box>
        <UPInlineDropdown
          items={dropdownItems}
          value={reasonFilter}
          onChange={setReasonFilter}
          id="no-show-kpi-reason"
          color="#9EA2A2"
        />
      </NoShowKpiHeader>
      <NoShowKpiChartContainer>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 24, left: 12, right: 12, bottom: 0 }}>
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{ fontSize: 12, fill: '#9EA2A2' }}
                tickMargin={6}
              />
              <YAxis hide domain={[0, 100]} />
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none', zIndex: 1000 }} cursor={false} />
              <Bar dataKey="percentage" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {chartData.map((data, index) => (
                  <Cell key={data.name} fill={index === currentWeekIndex ? '#008C15' : '#71B790'} />
                ))}
                <LabelList
                  dataKey="percentage"
                  position="top"
                  formatter={(value: number) => `${value}%`}
                  style={{
                    fontSize: '14px',
                    fontFamily: 'Montserrat,sans-serif',
                    fill: '#008C15',
                    fontWeight: 'bold',
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </NoShowKpiChartContainer>
    </NoShowKpiSection>
  );
};

export default DashboardNoShowKpiTile;
